import { useState } from "react";

interface FormField {
  id: string;
  label: string;
  type: "text" | "select" | "radio" | "checkbox" | "date" | "phone" | "email" | "textarea";
  required: boolean;
  enabled: boolean;
  placeholder?: string;
  options?: string[];
  isSystem?: boolean;
  order: number;
}

const defaultFields: FormField[] = [
  { id: "f1", label: "姓名", type: "text", required: true, enabled: true, placeholder: "请输入真实姓名", isSystem: true, order: 1 },
  { id: "f2", label: "性别", type: "radio", required: true, enabled: true, options: ["男", "女"], isSystem: true, order: 2 },
  { id: "f3", label: "出生日期", type: "date", required: true, enabled: true, isSystem: true, order: 3 },
  { id: "f4", label: "手机号码", type: "phone", required: true, enabled: true, placeholder: "请输入手机号码", isSystem: true, order: 4 },
  { id: "f5", label: "电子邮箱", type: "email", required: false, enabled: true, placeholder: "请输入邮箱地址", isSystem: true, order: 5 },
  { id: "f6", label: "身份证号", type: "text", required: true, enabled: true, placeholder: "请输入身份证号码", isSystem: true, order: 6 },
  { id: "f7", label: "居住城市", type: "text", required: true, enabled: true, placeholder: "如：某某市某某区", order: 7 },
  { id: "f8", label: "职业", type: "select", required: false, enabled: true, options: ["学生", "职员", "个体经营", "自由职业", "其他"], order: 8 },
  { id: "f9", label: "支持球队年限", type: "select", required: false, enabled: true, options: ["1年以内", "1-3年", "3-5年", "5年以上"], order: 9 },
  { id: "f10", label: "如何了解协会", type: "radio", required: false, enabled: false, options: ["朋友介绍", "社交媒体", "线下活动", "其他"], order: 10 },
  { id: "f11", label: "个人简介", type: "textarea", required: false, enabled: false, placeholder: "简单介绍自己（可选）", order: 11 },
];

const typeLabels: Record<FormField["type"], string> = {
  text: "文本",
  select: "下拉选择",
  radio: "单选",
  checkbox: "多选",
  date: "日期",
  phone: "手机号",
  email: "邮箱",
  textarea: "多行文本",
};

export default function FormFieldSettings() {
  const [fields, setFields] = useState<FormField[]>(defaultFields);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newField, setNewField] = useState<Partial<FormField>>({
    label: "", type: "text", required: false, enabled: true, placeholder: "", options: [],
  });
  const [optionInput, setOptionInput] = useState("");
  const [saved, setSaved] = useState(false);
  const [editOptionInput, setEditOptionInput] = useState("");

  const toggleEnabled = (id: string) => {
    setFields((p) => p.map((f) => f.id === id ? { ...f, enabled: !f.enabled } : f));
  };

  const toggleRequired = (id: string) => {
    setFields((p) => p.map((f) => f.id === id && !f.isSystem ? { ...f, required: !f.required } : f));
  };

  const handleMoveUp = (id: string) => {
    setFields((p) => {
      const idx = p.findIndex((f) => f.id === id);
      if (idx <= 0) return p;
      const arr = [...p];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      return arr;
    });
  };

  const handleMoveDown = (id: string) => {
    setFields((p) => {
      const idx = p.findIndex((f) => f.id === id);
      if (idx >= p.length - 1) return p;
      const arr = [...p];
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      return arr;
    });
  };

  const handleDelete = (id: string) => {
    setFields((p) => p.filter((f) => f.id !== id));
  };

  const handleAddField = () => {
    if (!newField.label) return;
    const f: FormField = {
      id: `f${Date.now()}`,
      label: newField.label!,
      type: newField.type as FormField["type"],
      required: newField.required!,
      enabled: true,
      placeholder: newField.placeholder,
      options: newField.options,
      order: fields.length + 1,
    };
    setFields((p) => [...p, f]);
    setShowAdd(false);
    setNewField({ label: "", type: "text", required: false, enabled: true, placeholder: "", options: [] });
    setOptionInput("");
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleSaveEdit = () => {
    if (!editingField) return;
    setFields((p) => p.map((f) => f.id === editingField.id ? editingField : f));
    setEditingField(null);
  };

  const needsOptions = (type: FormField["type"] | undefined) =>
    type === "select" || type === "radio" || type === "checkbox";

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-semibold text-gray-800">会员报名表字段设置</h3>
          <p className="text-xs text-gray-400 mt-0.5">配置会员报名时需要填写的字段内容</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i> 新增字段
          </button>
          <button
            onClick={handleSave}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap ${
              saved ? "bg-green-500 text-white" : "bg-red-600 text-white hover:bg-red-700"
            }`}
          >
            <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
            {saved ? "已保存" : "保存配置"}
          </button>
        </div>
      </div>

      {/* Preview hint */}
      <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-4 text-xs text-amber-700">
        <i className="ri-lightbulb-line flex-shrink-0"></i>
        <span>系统字段（带锁图标）为必填项，无法禁用。拖拽排序请使用上下箭头。</span>
      </div>

      {/* Field List */}
      <div className="space-y-2">
        {fields.map((field, idx) => (
          <div
            key={field.id}
            className={`bg-white rounded-xl p-4 flex items-center gap-3 transition-all ${
              !field.enabled ? "opacity-50" : ""
            }`}
          >
            {/* Order */}
            <div className="flex flex-col gap-0.5 flex-shrink-0">
              <button
                onClick={() => handleMoveUp(field.id)}
                disabled={idx === 0}
                className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-up-s-line text-sm"></i>
              </button>
              <button
                onClick={() => handleMoveDown(field.id)}
                disabled={idx === fields.length - 1}
                className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-down-s-line text-sm"></i>
              </button>
            </div>

            {/* Index */}
            <span className="w-6 text-center text-xs text-gray-400 flex-shrink-0">{idx + 1}</span>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-800">{field.label}</span>
                {field.isSystem && <i className="ri-lock-line text-xs text-gray-400"></i>}
                {field.required && (
                  <span className="text-xs text-red-500 font-medium">*必填</span>
                )}
                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                  {typeLabels[field.type]}
                </span>
              </div>
              {field.options && field.options.length > 0 && (
                <p className="text-xs text-gray-400 mt-0.5 truncate">
                  选项：{field.options.join(" / ")}
                </p>
              )}
              {field.placeholder && (
                <p className="text-xs text-gray-400 mt-0.5 truncate">提示：{field.placeholder}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {!field.isSystem && (
                <button
                  onClick={() => toggleRequired(field.id)}
                  className={`px-2 py-1 text-xs rounded-lg cursor-pointer transition-colors whitespace-nowrap ${
                    field.required ? "bg-red-50 text-red-500 hover:bg-red-100" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  {field.required ? "必填" : "选填"}
                </button>
              )}
              <button
                onClick={() => setEditingField({ ...field })}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100 transition-colors"
              >
                <i className="ri-edit-line text-sm"></i>
              </button>
              {!field.isSystem && (
                <button
                  onClick={() => handleDelete(field.id)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer rounded-lg hover:bg-red-50 transition-colors"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              )}
              <button
                onClick={() => toggleEnabled(field.id)}
                disabled={field.isSystem}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 disabled:cursor-not-allowed ${
                  field.enabled ? "bg-red-500" : "bg-gray-300"
                } disabled:opacity-50`}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${field.enabled ? "left-5" : "left-0.5"}`}></span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Field Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-[480px] max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-gray-800 mb-5">新增报名字段</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">字段名称 *</label>
                <input
                  type="text"
                  value={newField.label}
                  onChange={(e) => setNewField((p) => ({ ...p, label: e.target.value }))}
                  placeholder="如：紧急联系人"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">字段类型</label>
                <select
                  value={newField.type}
                  onChange={(e) => setNewField((p) => ({ ...p, type: e.target.value as FormField["type"] }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 bg-white"
                >
                  {(Object.entries(typeLabels) as [FormField["type"], string][]).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              {!needsOptions(newField.type) && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">占位提示</label>
                  <input
                    type="text"
                    value={newField.placeholder}
                    onChange={(e) => setNewField((p) => ({ ...p, placeholder: e.target.value }))}
                    placeholder="请输入..."
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                  />
                </div>
              )}
              {needsOptions(newField.type) && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">选项（回车添加）</label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {(newField.options || []).map((opt, i) => (
                      <span key={i} className="flex items-center gap-1 text-xs bg-red-50 text-red-600 px-2.5 py-1 rounded-full">
                        {opt}
                        <button onClick={() => setNewField((p) => ({ ...p, options: p.options?.filter((_, j) => j !== i) }))} className="cursor-pointer hover:text-red-800">×</button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={optionInput}
                    onChange={(e) => setOptionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && optionInput.trim()) {
                        setNewField((p) => ({ ...p, options: [...(p.options || []), optionInput.trim()] }));
                        setOptionInput("");
                      }
                    }}
                    placeholder="输入选项按回车添加"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                  />
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newField.required}
                  onChange={(e) => setNewField((p) => ({ ...p, required: e.target.checked }))}
                  className="w-4 h-4 rounded accent-red-600"
                />
                <span className="text-sm text-gray-600">设为必填项</span>
              </label>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleAddField} className="px-4 py-2 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap">添加字段</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Field Modal */}
      {editingField && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-[480px] max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-gray-800 mb-5">编辑字段：{editingField.label}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">字段名称</label>
                <input
                  type="text"
                  value={editingField.label}
                  onChange={(e) => setEditingField((p) => p ? { ...p, label: e.target.value } : null)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
              {!needsOptions(editingField.type) && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">占位提示</label>
                  <input
                    type="text"
                    value={editingField.placeholder || ""}
                    onChange={(e) => setEditingField((p) => p ? { ...p, placeholder: e.target.value } : null)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                  />
                </div>
              )}
              {needsOptions(editingField.type) && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">选项管理</label>
                  <div className="space-y-1.5 mb-2">
                    {(editingField.options || []).map((opt, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={opt}
                          onChange={(e) => setEditingField((p) => {
                            if (!p) return null;
                            const opts = [...(p.options || [])];
                            opts[i] = e.target.value;
                            return { ...p, options: opts };
                          })}
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-red-400"
                        />
                        <button
                          onClick={() => setEditingField((p) => p ? { ...p, options: p.options?.filter((_, j) => j !== i) } : null)}
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer"
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editOptionInput}
                      onChange={(e) => setEditOptionInput(e.target.value)}
                      placeholder="输入新选项"
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-red-400"
                    />
                    <button
                      onClick={() => {
                        if (!editOptionInput.trim()) return;
                        setEditingField((p) => p ? { ...p, options: [...(p.options || []), editOptionInput.trim()] } : null);
                        setEditOptionInput("");
                      }}
                      className="px-3 py-1.5 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap"
                    >
                      添加
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setEditingField(null)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleSaveEdit} className="px-4 py-2 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap">保存修改</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
